/**
 * PATH: packages/ui/src/kanban/components/MeetingSuggestionCard.tsx
 * FILE_ID: UI-KANBAN-MEETING-SUGGESTION-CARD-001
 * PURPOSE: Render one AI meeting suggestion with Accept / Dismiss controls.
 * WHY: FEAT-2026-06-15-BOARD-MEETING-MODE Phase C — operator triages live
 *   AI proposals (action → checklist item, card change, mention) as they talk.
 */
import { ArrowUpRight, Check, ListChecks, PencilLine, Tag, X } from 'lucide-react'
import { memo } from 'react'

import type { MeetingSuggestion } from '../meetingTypes'

export interface MeetingSuggestionCardProps {
  suggestion: MeetingSuggestion
  onAccept: (id: string) => void
  onDismiss: (id: string) => void
  /**
   * Jump to the suggestion's target card on the board (scroll + flash). Only
   * wired when the suggestion carries a `target_card_id`; omit to render the
   * title as plain text (back-compat for callers that don't support jumping).
   */
  onJump?: (cardId: string) => void
}

const KIND_META: Record<
  MeetingSuggestion['kind'],
  { label: string; icon: typeof Check; accent: string }
> = {
  action: { label: 'Action', icon: ListChecks, accent: 'text-emerald-600 dark:text-emerald-400' },
  card_change: { label: 'Card change', icon: PencilLine, accent: 'text-amber-600 dark:text-amber-400' },
  mention: { label: 'Mentioned', icon: Tag, accent: 'text-sky-600 dark:text-sky-400' },
}

function MeetingSuggestionCardBase({
  suggestion,
  onAccept,
  onDismiss,
  onJump,
}: MeetingSuggestionCardProps) {
  const meta = KIND_META[suggestion.kind] ?? KIND_META.action
  const Icon = meta.icon
  const acceptLabel =
    suggestion.kind === 'action'
      ? 'Add'
      : suggestion.kind === 'card_change'
        ? 'Apply'
        : 'Keep'
  const titleText = suggestion.title || suggestion.detail || '(untitled)'
  const cardId = suggestion.target_card_id
  const jumpable = Boolean(cardId && onJump)

  return (
    <div className="rounded-md border border-neutral-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-2.5 shadow-sm">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`h-3.5 w-3.5 ${meta.accent}`} aria-hidden />
        <span className={`text-[10px] font-semibold uppercase tracking-wide ${meta.accent}`}>
          {meta.label}
        </span>
        {suggestion.due_date && (
          <span className="ml-auto text-[10px] text-neutral-500 dark:text-neutral-400">
            due {suggestion.due_date}
          </span>
        )}
      </div>
      {jumpable ? (
        <button
          type="button"
          onClick={() => onJump?.(cardId as string)}
          title="Jump to this card on the board"
          aria-label="Jump to this card on the board"
          className="group flex w-full items-start gap-1 text-left"
        >
          <span className="text-xs font-medium text-neutral-900 dark:text-neutral-100 leading-snug group-hover:text-blue-700 dark:group-hover:text-blue-400 group-hover:underline">
            {titleText}
          </span>
          <ArrowUpRight
            className="mt-0.5 h-3 w-3 shrink-0 text-neutral-400 group-hover:text-blue-600 dark:group-hover:text-blue-400"
            aria-hidden
          />
        </button>
      ) : (
        <p className="text-xs font-medium text-neutral-900 dark:text-neutral-100 leading-snug">
          {titleText}
        </p>
      )}
      {suggestion.detail && suggestion.title && (
        <p className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400 leading-snug">
          {suggestion.detail}
        </p>
      )}
      <div className="mt-2 flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onAccept(suggestion.id)}
          className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700"
        >
          <Check className="h-3 w-3" aria-hidden /> {acceptLabel}
        </button>
        <button
          type="button"
          onClick={() => onDismiss(suggestion.id)}
          className="inline-flex items-center gap-1 rounded border border-neutral-200 dark:border-zinc-700 px-2 py-1 text-[11px] font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-zinc-800"
        >
          <X className="h-3 w-3" aria-hidden /> Dismiss
        </button>
      </div>
    </div>
  )
}

export const MeetingSuggestionCard = memo(MeetingSuggestionCardBase)
